/**
 * Checklist template management. Templates are recipes; instances copy them, so deleting a
 * template never touches a running project's checklists.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type ChecklistTemplateInfo } from "../../api";
import { Dialog } from "../../components/Dialog";
import { Button, Field, Num } from "../../components/primitives";
import { useToast } from "../../components/toast";

export function TemplateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const showToast = useToast();

  const [templates, setTemplates] = useState<ChecklistTemplateInfo[]>([]);
  const [name, setName] = useState("");
  const [itemsText, setItemsText] = useState("");

  useEffect(() => {
    if (open) {
      void api.checklistTemplates().then(setTemplates);
    }
  }, [open]);

  const create = useCallback(async () => {
    const items = itemsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (!name.trim() || items.length === 0) {
      return;
    }
    try {
      const created = await api.createChecklistTemplate({ name: name.trim(), items });
      setTemplates((current) =>
        [...current, created].sort((a, b) => a.name.localeCompare(b.name, "sv")),
      );
      setName("");
      setItemsText("");
    } catch (error) {
      showToast({
        message: error instanceof ApiError ? error.message : "Mallen kunde inte skapas.",
        tone: "fail",
      });
    }
  }, [name, itemsText, showToast]);

  const remove = useCallback(
    async (template: ChecklistTemplateInfo) => {
      await api.removeChecklistTemplate(template.id);
      setTemplates((current) => current.filter((t) => t.id !== template.id));
      showToast({
        message: `Mallen ”${template.name}” togs bort.`,
        undo: () => {
          void api
            .createChecklistTemplate({ name: template.name, items: template.items })
            .then((recreated) =>
              setTemplates((current) =>
                [...current, recreated].sort((a, b) => a.name.localeCompare(b.name, "sv")),
              ),
            );
        },
      });
    },
    [showToast],
  );

  return (
    <Dialog
      open={open}
      title="Checklistmallar"
      subtitle="Mall → instans per projekt"
      onClose={onClose}
    >
      <div className="grid gap-s6">
        {templates.length === 0 ? (
          <p className="text-body text-muted">
            Inga mallar ännu. Skapa en nedan — till exempel ”Platsbesök” eller ”Egenkontroll”.
          </p>
        ) : (
          <ul className="grid gap-s2">
            {templates.map((template) => (
              <li
                key={template.id}
                className="flex items-center gap-s4 rounded-control px-s3 py-s2 hover:bg-surface-2"
              >
                <span className="text-body text-ink">{template.name}</span>
                <span className="text-small text-muted">
                  <Num>{template.items.length}</Num> punkter
                </span>
                <button
                  type="button"
                  aria-label={`Ta bort mallen ${template.name}`}
                  className="ml-auto text-small text-muted hover:text-ink"
                  onClick={() => void remove(template)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <form
          className="grid gap-s4 border-t border-line pt-s5"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Field
            label="Ny mall"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <label className="grid gap-s2">
            <span className="nb-overline border-0 pb-0">Punkter — en per rad</span>
            <textarea
              rows={4}
              className="nb-field min-h-[96px] resize-y py-s3"
              value={itemsText}
              onChange={(event) => setItemsText(event.target.value)}
            />
          </label>
          <Button
            variant="primary"
            type="submit"
            className="justify-self-end"
            disabled={!name.trim() || !itemsText.trim()}
          >
            Skapa mall
          </Button>
        </form>
      </div>
    </Dialog>
  );
}
